import React, { useEffect, useRef } from 'react';
import { useLayoutContext } from '../../layouts/general-layout';

interface ScrollableBookLayoutProps {
  bookContent: any;
}

const ScrollableBookLayout: React.FC<ScrollableBookLayoutProps> = ({ bookContent }) => {
  const { t } = useLayoutContext();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [bookContent]);

  const renderPage = (page: any, index: number) => {
    const pageKey = Object.keys(page)[0];
    const pageContent = page[pageKey];
    const hasImage = typeof pageContent === 'object' && 
                    pageContent.image_url && 
                    typeof pageContent.image_url === 'string' && 
                    pageContent.image_url.trim() !== '';

    if (pageKey === 'page_1') {
      return (
        <div key={index} className="scroll-page title-page">
          <div className="page-content">
            <h2>{pageContent.title}</h2>
            <p className="author">{t('by')} {pageContent.author}</p>
          </div>
        </div>
      );
    } else if (pageKey === 'page_2') {
      return (
        <div key={index} className="scroll-page dedication-page">
          <div className="page-content">
            <h3>{t('dedicationLabel')}</h3>
            <p>
              {(() => {
                if (typeof pageContent === 'object' && pageContent.dedication) {
                  return pageContent.dedication;
                } else if (typeof pageContent === 'string') {
                  return pageContent;
                } else {
                  return t('none');
                }
              })()}
            </p>
          </div>
        </div>
      );
    } else {
      if (hasImage) {
        const fullImageUrl = pageContent.image_url.trim();
        
        return (
          <React.Fragment key={index}>
            <div className="scroll-page content-page">
              <div className="page-content">
                <p>
                  {typeof pageContent.content === 'string' ? pageContent.content : JSON.stringify(pageContent.content)}
                </p>
              </div>
            </div>
            <div className="scroll-page image-page">
              <div className="page-content">
                <div className="image-container">
                  <img 
                    src={fullImageUrl} 
                    alt="Story illustration" 
                    className="story-image"
                    onError={(e) => {
                      const target = e.currentTarget as HTMLImageElement;
                      target.style.display = 'none';
                      const fallback = target.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'block';
                    }}
                  />
                  <div className="image-fallback" style={{ display: 'none' }}>
                    <p>{t('imageLoadingError')}</p>
                  </div>
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      } else {
        return (
          <div key={index} className="scroll-page content-page">
            <div className="page-content">
              <p>
                {typeof pageContent === 'string' ? pageContent : JSON.stringify(pageContent)}
              </p>
            </div>
          </div>
        );
      }
    }
  };

  return (
    <div className="scrollable-book-layout" ref={scrollContainerRef}>
      {(() => {
        const pages: React.ReactElement[] = [];
        const items = bookContent?.pages || [];

        items.forEach((page: any, index: number) => {
          const pageKey = Object.keys(page)[0];
          const pageContent = page[pageKey];

          if (pageKey === 'page_1') {
            const frontCoverImageUrl = typeof pageContent === 'object' && 
                                      pageContent.front_cover_image_url && 
                                      typeof pageContent.front_cover_image_url === 'string' && 
                                      pageContent.front_cover_image_url.trim() !== ''
                                      ? pageContent.front_cover_image_url.trim()
                                      : null;
            
            const titlePageClass = frontCoverImageUrl ? 'scroll-page title-page has-cover-image' : 'scroll-page title-page';
            
            pages.push(
              <div key={`title-${index}`} className={titlePageClass}>
                {frontCoverImageUrl && (
                  <img 
                    src={frontCoverImageUrl}
                    alt="Front cover"
                    className="cover-image"
                    onError={(e) => {
                      // Image failed to load
                    }}
                  />
                )}
                <div className="page-content">
                  <h2>{pageContent.title}</h2>
                  <p className="author">{t('by')} {pageContent.author}</p>
                </div>
              </div>
            );

            pages.push(
              <div key={`blank-after-title-${index}`} className="scroll-page blank-page">
                <div className="page-content" />
              </div>
            );
          } else if (pageKey === 'page_2') {
            pages.push(
              <div key={`dedication-${index}`} className="scroll-page dedication-page">
                <div className="page-content">
                  <h3>{t('dedicationLabel')}</h3>
                  <p>
                    {(() => {
                      if (typeof pageContent === 'object' && pageContent.content) {
                        return pageContent.content;
                      } else if (typeof pageContent === 'string') {
                        return pageContent;
                      } else {
                        return t('none');
                      }
                    })()}
                  </p>
                </div>
              </div>
            );
          } else {
            const hasImage = typeof pageContent === 'object' && pageContent?.image_url && typeof pageContent.image_url === 'string' && pageContent.image_url.trim() !== '';

            pages.push(
              <div key={`content-${index}`} className="scroll-page content-page">
                <div className="page-content">
                  <p>
                    {typeof pageContent === 'object' && 'content' in pageContent
                      ? (typeof pageContent.content === 'string' ? pageContent.content : JSON.stringify(pageContent.content))
                      : (typeof pageContent === 'string' ? pageContent : JSON.stringify(pageContent))}
                  </p>
                  {pageContent.cta_option_a && pageContent.cta_option_b && (
                    <div className="cta-buttons">
                      <button className="cta-btn cta-option-a">{pageContent.cta_option_a}</button>
                      <button className="cta-btn cta-option-b">{pageContent.cta_option_b}</button>
                    </div>
                  )}                  
                </div>
              </div>             
            );

            if (hasImage) {
              const fullImageUrl = pageContent.image_url.trim();
              pages.push(
                <div key={`image-${index}`} className="scroll-page image-page">
                  <div className="page-content">
                    <div className="image-container">
                      <img
                        src={fullImageUrl}
                        alt="Story illustration"
                        className="story-image"
                        onError={(e) => {
                          const target = e.currentTarget as HTMLImageElement;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) fallback.style.display = 'block';
                        }}
                      />
                      <div className="image-fallback" style={{ display: 'none' }}>
                        <p>{t('imageLoadingError')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
          }
        });

        return pages;
      })()}
    </div>
  );
};

export default ScrollableBookLayout;
